import TimeLayer from "./TimeLayer";

import moment from "moment-timezone";
import NysmTimeSeries2 from "../Dataset/NysmTimeSeries2";

class NysmApiLayer extends TimeLayer {
  constructor(opt_options) {
    const options = opt_options || {};
    const baseOptions = Object.assign({}, options);
    delete baseOptions.dataset;
    delete baseOptions.subset;
    delete baseOptions.varid;
    delete baseOptions.stations;
    delete baseOptions.mapProjection;
    delete baseOptions.layerMakerCallback;
    super(baseOptions);

    this._dataset = options.dataset;
    this._subset = options.subset;
    this._varid = options.varid;
    this._stations = options.stations;
    this._mapProjection = options.mapProjection;
    this._layerMakerCallback = options.layerMakerCallback;
    this._datasetProvider = new NysmTimeSeries2(this._dataset, {
      stations: this._stations,
      subset: this._subset,
    });
    super.setStartTimeAllowableDelta(this._datasetProvider.realtime_lag);

    this._loaded_times = new Set();
    this._rtTimeManager = null;

    this.load = this.load.bind(this);
    this._realtime_data_received = this._realtime_data_received.bind(this);

    this._subscribedToTracking = false;
  }

  load(timeManager) {
    const now = moment.utc();
    let endTime = timeManager.end_date;
    const startTimeDelta = timeManager.start_date
      .clone()
      .subtract(this._datasetProvider.realtime_lag);
    // detect realtime request
    if (
      timeManager.isTracking ||
      now.clone().subtract(this._datasetProvider.realtime_lag) < endTime
    ) {
      endTime = null;
    }
    // if realtime request and we are tracking realtime, see if we can
    // skip this one
    if (
      endTime === null &&
      this._subscribedToTracking &&
      (timeManager.start_date.isSame(timeManager.end_date) ||
        [...this._loaded_times].sort()[0] <=
          timeManager.start_date
            .clone()
            .subtract(this._datasetProvider.data_interval)
            .valueOf())
    ) {
      return;
    }
    this._datasetProvider
      .get_data_in_time_range(this._varid, startTimeDelta, endTime)
      .then((result) => {
        const new_times = [];
        result.coords.time.data.forEach((time, i) => {
          const momentTime = moment.tz(time, "UTC");
          new_times.push(momentTime);
        });
        this._layer_times_received(new_times, timeManager);
      });
  }

  _layer_times_received(new_times, timeManager) {
    const startTime = timeManager.start_date;
    const endTime = timeManager.end_date;
    const startTimeDelta = timeManager.start_date
      .clone()
      .subtract(this._datasetProvider.realtime_lag);
    // Filter to include only the range we care about.
    let addedOneEarly = false;
    let times = [];
    try {
      times = new_times.reverse().filter((t) => {
        if (t.isSameOrAfter(startTime) && t.isSameOrBefore(endTime)) {
          if (t.isSame(startTime)) {
            // Don't need to add one early, flag it as done
            addedOneEarly = true;
          }
          return true;
        }
        if (
          t.isBefore(startTime) &&
          t.isSameOrAfter(startTimeDelta) &&
          !addedOneEarly
        ) {
          addedOneEarly = true;
          return true;
        }
        return false;
      });
    } catch (err) {
      console.error(err);
      times = [];
    }
    // Filter times down to those that don't already have layers (or have layers pending), then
    // create layers for any new times.
    const times_loaded = this.getLayers()
      .getArray()
      .map((l) => l.get("time").valueOf());
    times
      .filter((t) => times_loaded.indexOf(t.valueOf()) === -1)
      .forEach((t) => {
        this._loaded_times.add(t.valueOf());
        let wait = null;
        if (this._layerMakerCallback) {
          wait = this._layerMakerCallback(t, this._mapProjection);
        } else {
          wait = new Promise((res) => res(null));
        }
        wait.then((new_layer) => {
          if (new_layer === null) {
            console.warn("No layer to add");
            return;
          }
          this.getLayers().push(new_layer);
        });
      });
  }

  track_realtime(isTracking, timeManager) {
    if (!isTracking && this._subscribedToTracking) {
      this._datasetProvider.remove_realtime_tracker(
        this._realtime_data_received
      );
      this._rtTimeManager = null;
    } else if (isTracking && !this._subscribedToTracking) {
      this._rtTimeManager = timeManager;
      this._datasetProvider.add_realtime_tracker(this._realtime_data_received);
    }
    this._subscribedToTracking = isTracking;
  }

  _realtime_data_received(newTime, result) {
    this._layer_times_received([newTime], this._rtTimeManager);
  }
}

export default NysmApiLayer;
